import {
  BinaryExpression,
  FieldOperators,
  GroupOperators,
  PostCallAnalysisBinaryCondition,
  PostCallFinalizedActionConditionType,
  PostCallFinalizedActionConditions,
  PostCallAnalysisItem,
} from 'src/interfaces/agent.interface';
import { HumanDetectionResult } from 'src/interfaces/call.interface';
import {
  CallCondition,
  PostCallContainsCondition,
  PostCallDataExtractionCondition,
  PostCallJsonPropertyCondition,
  UiCondition,
  UnselectedCondition,
  generateConditionId,
  UiConditionType,
} from './types';
import { coerceAndValidateRight } from './postCallDataExtractionRules';

export const CALL_HUMAN_DETECTION_OPERAND =
  '{{params.call.human_detection_result}}';
export const POST_CALL_RESULTS_CONTENT_OPERAND =
  '{{params.call.post_call_results.content}}';

const INCOMPLETE_CONDITIONS_ERROR_MESSAGE =
  'Please complete or remove incomplete conditions.';

export class IncompleteConditionsError extends Error {
  constructor(message = INCOMPLETE_CONDITIONS_ERROR_MESSAGE) {
    super(message);
    this.name = 'IncompleteConditionsError';
  }
}

// Escape a string for safe embedding inside a JSON string literal
const escapeJsonString = (value: string): string =>
  String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export const POST_CALL_RESULTS_JSON_PATH_OPERAND = (path: string) =>
  `{{getFromJson({"json": params.call.post_call_results.content, "path": "${escapeJsonString(path)}"})}}`;

const parsePostCallResultsJsonPathOperand = (left: string): string | null => {
  // Matches the JSON string value for "path", allowing escaped quotes
  const regex =
    /getFromJson\(\{"json":\s*params\.call\.post_call_results\.content,\s*"path":\s*"((?:\\.|[^"\\])*)"\}\)\}\}/;
  const match = left.match(regex);
  if (!match) return null;
  const raw = match[1];
  try {
    // Safely unescape using JSON.parse on a quoted string
    return JSON.parse(`"${raw}"`);
  } catch {
    return raw;
  }
};

export const groupFilterToUiConditions = (
  filter?: PostCallFinalizedActionConditions | null,
): UiCondition[] => {
  if (!filter || !Array.isArray(filter.children)) return [];

  return filter.children.map<UiCondition>((expr) => {
    const id = generateConditionId();

    if (
      typeof expr === 'object' &&
      expr !== null &&
      'type' in expr &&
      expr.type ===
        PostCallFinalizedActionConditionType.ANALYSIS_ITEM_BINARY_EXPRESSION
    ) {
      const analysisExpr = expr as PostCallAnalysisBinaryCondition;
      const right = analysisExpr.right;
      const uiRight: PostCallDataExtractionCondition['right'] =
        typeof right === 'number' ? String(right) : (right ?? null);

      const cond: PostCallDataExtractionCondition = {
        id,
        type: UiConditionType.POST_CALL_DATA_EXTRACTION,
        analysisItemId: analysisExpr.analysisItemId,
        operator: analysisExpr.operator,
        right: uiRight,
      };
      return cond;
    }

    const binaryExpr = expr as BinaryExpression;

    if (
      binaryExpr.left === CALL_HUMAN_DETECTION_OPERAND &&
      binaryExpr.operator === FieldOperators.EQ &&
      (binaryExpr.right === HumanDetectionResult.NO_HUMAN ||
        binaryExpr.right === HumanDetectionResult.HUMAN)
    ) {
      const humanDetectionResult = binaryExpr.right as HumanDetectionResult;
      const cond: CallCondition = {
        id,
        type: UiConditionType.CALL,
        humanDetectionResult,
      };
      return cond;
    }

    if (
      binaryExpr.left === POST_CALL_RESULTS_CONTENT_OPERAND &&
      binaryExpr.operator === FieldOperators.CONTAINS
    ) {
      const cond: PostCallContainsCondition = {
        id,
        type: UiConditionType.POST_CALL_CONTAINS,
        searchString: String(binaryExpr.right ?? ''),
      };
      return cond;
    }

    if (
      typeof binaryExpr.left === 'string' &&
      binaryExpr.left.includes('getFromJson') &&
      binaryExpr.left.includes('post_call_results.content')
    ) {
      const path =
        parsePostCallResultsJsonPathOperand(String(binaryExpr.left)) ?? '';
      const cond: PostCallJsonPropertyCondition = {
        id,
        type: UiConditionType.POST_CALL_JSON_PROPERTY,
        path,
        operator: binaryExpr.operator as FieldOperators,
        value: String(binaryExpr.right ?? ''),
      };
      return cond;
    }

    const fallback: UnselectedCondition = {
      id,
      type: UiConditionType.UNSELECTED,
    };
    return fallback;
  });
};

export const uiConditionsToGroupFilter = (
  uiConditions: UiCondition[],
  options?: {
    postCallAnalysisItems?: PostCallAnalysisItem[];
  },
): PostCallFinalizedActionConditions | null => {
  const children: Array<BinaryExpression | PostCallAnalysisBinaryCondition> =
    [];
  const postCallAnalysisItems = options?.postCallAnalysisItems ?? [];

  for (const c of uiConditions) {
    if (c.type === UiConditionType.UNSELECTED) continue;

    if (c.type === UiConditionType.CALL) {
      if (!c.humanDetectionResult) throw new IncompleteConditionsError();
      children.push({
        left: CALL_HUMAN_DETECTION_OPERAND,
        operator: FieldOperators.EQ,
        right: c.humanDetectionResult,
      });
      continue;
    }

    if (c.type === UiConditionType.POST_CALL_CONTAINS) {
      if (!c.searchString || c.searchString.trim() === '')
        throw new IncompleteConditionsError();
      children.push({
        left: POST_CALL_RESULTS_CONTENT_OPERAND,
        operator: FieldOperators.CONTAINS,
        right: c.searchString,
      });
      continue;
    }

    if (c.type === UiConditionType.POST_CALL_DATA_EXTRACTION) {
      const cond = c as PostCallDataExtractionCondition;
      if (!cond.analysisItemId || cond.analysisItemId.trim() === '')
        throw new IncompleteConditionsError();

      const operator = cond.operator ?? FieldOperators.EQ;
      const right = cond.right;
      if (right === null || right === undefined)
        throw new IncompleteConditionsError();
      if (typeof right === 'string' && right.trim() === '')
        throw new IncompleteConditionsError();

      const item = postCallAnalysisItems.find(
        (i) => i.id === cond.analysisItemId,
      );
      if (!item) {
        children.push({
          type: PostCallFinalizedActionConditionType.ANALYSIS_ITEM_BINARY_EXPRESSION,
          analysisItemId: cond.analysisItemId,
          operator,
          right,
        });
        continue;
      }

      const coerced = coerceAndValidateRight({
        item,
        operator,
        rightInput: right,
      });

      if (!coerced.ok) {
        throw new Error(coerced.message);
      }

      children.push({
        type: PostCallFinalizedActionConditionType.ANALYSIS_ITEM_BINARY_EXPRESSION,
        analysisItemId: cond.analysisItemId,
        operator,
        right: coerced.right,
      });
      continue;
    }

    if (c.type === UiConditionType.POST_CALL_JSON_PROPERTY) {
      if (!c.path || c.path.trim() === '')
        throw new IncompleteConditionsError();
      if (!c.value || c.value.trim() === '')
        throw new IncompleteConditionsError();

      const left = POST_CALL_RESULTS_JSON_PATH_OPERAND(c.path);
      const operator = c.operator ?? FieldOperators.EQ;
      children.push({ left, operator, right: c.value });
      continue;
    }
  }

  if (children.length === 0) return null;

  return {
    groupOperator: GroupOperators.AND,
    children,
  };
};
